// Port of webapp/src/components/kanban/kanbanColumnHeader.tsx

import {ReactElement} from 'react'
import {IPropertyOption, IPropertyTemplate} from '../../types/board'
import {Constants} from '../../common/constants'
import {Icon} from '../common/icons'
import {ButtonWithMenu, Menu, MenuItem, MenuSeparator} from '../common/menu'
import {optionColorCssVar} from '../../state/properties'
import Editable from '../common/editable'

type Props = {
    groupByProperty: IPropertyTemplate | undefined
    option: IPropertyOption
    cardCount: number
    isCollapsed: boolean
    readOnly: boolean
    onTitleChange: (option: IPropertyOption, value: string) => void
    onColorChange: (option: IPropertyOption, color: string) => void
    onToggleCollapsed: (optionId: string) => void
    onHideGroup: (option: IPropertyOption) => void
    onAddCard: (optionId: string) => void
    onAddOption: () => void
}

const colorChoices = Object.entries(Constants.menuColors)

export default function KanbanColumnHeader(props: Props): ReactElement {
    const {option, cardCount, groupByProperty, isCollapsed, readOnly, onAddCard} = props
    const canEditOption = groupByProperty?.type === 'select' && option.id !== '' && !readOnly

    return (
        <div className='octo-board-header-cell flex items-center gap-2' data-column-id={option.id || 'blank'}>
            {option.color && (
                <span
                    className='h-3 w-3 shrink-0 rounded-full'
                    style={{backgroundColor: optionColorCssVar(option.color)}}
                    aria-hidden={true}
                />
            )}
            {!option.color && option.id && (
                <span className='h-3 w-3 shrink-0 rounded-full border border-main-fg/30' aria-hidden={true}/>
            )}

            {canEditOption ? (
                <Editable
                    className='flex-1 font-semibold'
                    value={option.value}
                    onChange={(value) => props.onTitleChange(option, value)}
                    allowEmpty={false}
                />
            ) : (
                <span className='flex-1 truncate font-semibold' title={option.value}>
                    {option.value || `未设置${groupByProperty?.name || '分组'}`}
                </span>
            )}

            <span className='shrink-0 tabular-nums opacity-60' aria-label={`${cardCount} 张卡片`}>{cardCount}</span>

            {!readOnly && (
                <button
                    type='button'
                    className='IconButton shrink-0'
                    onClick={() => onAddCard(option.id)}
                    aria-label='添加卡片'
                    title='新建卡片'
                >
                    <Icon name='add' size={16}/>
                </button>
            )}

            {!readOnly && (
                <ButtonWithMenu
                    className='shrink-0'
                    button={
                        <span className='IconButton' aria-label='列选项'>
                            <Icon name='dots' size={16}/>
                        </span>
                    }
                >
                    <Menu ariaLabel='列选项菜单'>
                        <MenuItem
                            icon={isCollapsed ? 'chevron-down' : 'chevron-right'}
                            onClick={() => props.onToggleCollapsed(option.id)}
                        >
                            {isCollapsed ? '收起分组' : '展开分组'}
                        </MenuItem>
                        {option.id && (
                            <MenuItem
                                icon='eye-off'
                                onClick={() => props.onHideGroup(option)}
                            >
                                隐藏分组
                            </MenuItem>
                        )}
                        {canEditOption && (
                            <>
                                <MenuSeparator/>
                                <div className='px-3 py-1 text-xxs uppercase opacity-60'>颜色</div>
                                <div className='flex flex-wrap gap-1 px-3 pb-2'>
                                    {colorChoices.map(([token, label]) => (
                                        <button
                                            key={token}
                                            type='button'
                                            title={label}
                                            aria-label={`颜色：${label}`}
                                            className={
                                                option.color === token ?
                                                    'h-5 w-5 rounded-full ring-2 ring-offset-1 ring-main-fg/40' :
                                                    'h-5 w-5 rounded-full border border-main-fg/10'
                                            }
                                            style={{backgroundColor: optionColorCssVar(token)}}
                                            onClick={() => props.onColorChange(option, token)}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                        {groupByProperty?.type === 'select' && (
                            <>
                                <MenuSeparator/>
                                <MenuItem icon='add' onClick={props.onAddOption}>添加分组</MenuItem>
                            </>
                        )}
                    </Menu>
                </ButtonWithMenu>
            )}
        </div>
    )
}
